import styled from 'styled-components';
import { connect } from 'react-redux';
import BackButton from '../Shared/PreviousPageButton';
import SaveApplicationButton from '../Shared/SaveApplicationButton';
import {
  PrimaryButtonDarkTheme,
  LoadingSpinner,
  CONTENT_MAX_WIDTH,
  SIDEBAR_DESKTOP_OFFSET,
  SIDEBAR_TABLET_OFFSET,
  TABLET_HORIZONTAL_PADDING,
} from '../../elements';
import { isPageComplete } from '../../selectors/pageComplete';
import { getCurrentPage, getPaymentInterval } from '../../selectors/routing';
import { selectMonth1Owed, selectMonthlyPremium, selectTotalYearlyOwed } from '../../selectors/quote';
import { selectLoadingState, selectPurchasedPolicies } from '../../selectors';
import handleFormSubmit from '../../actions/handleFormSubmit';
import { clearApplication } from '../../actions/application';
import { PAGES, RATE_PAGES } from '../../constants';
import { DesktopAndTablet, Mobile } from '../../helpers/responsive';
import { formatCurrency } from '../../helpers/formatNumber';
import usePartnerWantsHidden from '../../hooks/usePartnerWantsHidden';
import useObserverFooterHeight from '../../hooks/useFooterHeight';

interface FooterProps {
  handleFormSubmit: () => void;
  clearApplication: () => void;
  isLoading: boolean;
  isPageComplete: boolean;
  isFirstPage: boolean;
  isSummaryPage: boolean;
  isPaymentPage: boolean;
  paymentInterval: string;
  isPolicyPurchased: boolean;
  month1Owed: number;
  totalYearlyOwed: number;
  monthlyPremium: number;
  isRatePage: boolean;
  applicationUrl?: string;
}

const Footer = ({
  handleFormSubmit,
  clearApplication,
  isLoading,
  isPageComplete,
  isFirstPage,
  isSummaryPage,
  isPaymentPage,
  paymentInterval,
  isPolicyPurchased,
  month1Owed,
  totalYearlyOwed,
  monthlyPremium,
  isRatePage,
  applicationUrl,
}: FooterProps) => {
  const isAnnual = paymentInterval === 'None';
  const totalDue: string = isAnnual ? formatCurrency(totalYearlyOwed) : formatCurrency(month1Owed);
  const premium = isAnnual ? totalYearlyOwed : monthlyPremium;
  const priceSuffix = isAnnual ? '/yr' : '/mo';
  const priceLabel = isAnnual ? 'est annual premium' : 'est monthly premium';
  const footerRef = useObserverFooterHeight();
  const { hideSaveForLaterStartOverButtons } = usePartnerWantsHidden();
  const isDisabled = !isPageComplete || isLoading;

  const NextButtonText = () => {
    if (isLoading) {
      return <LoadingSpinner />;
    } else if (isSummaryPage) {
      return <>Buy policy</>;
    } else if (isPaymentPage) {
      return <>Pay {totalDue}</>;
    } else {
      return <>Next</>;
    }
  };

  if (isPolicyPurchased && !hideSaveForLaterStartOverButtons) {
    return (
      <>
        <FalseFooter />
        <FooterBar ref={footerRef}>
          <ButtonCont justifyContent="center">
            <NextButton disabled={false} data-cy="submit" onClick={clearApplication} aria-label="start over">
              Start over
            </NextButton>
          </ButtonCont>
        </FooterBar>
      </>
    );
  }

  return (
    <>
      <FalseFooter />
      <FooterBar ref={footerRef}>
        <ButtonCont justifyContent={isFirstPage && !applicationUrl ? 'center' : 'space-between'}>
          {!isFirstPage && (
            <DesktopAndTablet>
              <BackButton />
            </DesktopAndTablet>
          )}
          <Mobile>
            {isRatePage && premium > 0 && (
              <PriceBlock>
                <PriceLabel>{priceLabel}</PriceLabel>
                <PriceAmount>
                  {formatCurrency(premium)}
                  {priceSuffix}
                </PriceAmount>
                {!hideSaveForLaterStartOverButtons && applicationUrl && (
                  <PriceBlockSaveWrapper>
                    <SaveApplicationButton shouldUseAltColor={false} />
                  </PriceBlockSaveWrapper>
                )}
              </PriceBlock>
            )}
          </Mobile>
          {!hideSaveForLaterStartOverButtons && applicationUrl && (
            <SaveForLater hiddenOnMobile={premium > 0}>
              <SaveApplicationButton shouldUseAltColor={false} />
            </SaveForLater>
          )}
          <NextButton
            disabled={isDisabled}
            fullWidth={!applicationUrl}
            data-cy="submit"
            onClick={handleFormSubmit}
            aria-label="next page"
            data-pendo={isPaymentPage ? 'pay-button' : 'next-button'}
          >
            <NextButtonText />
          </NextButton>
        </ButtonCont>
      </FooterBar>
    </>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  isLoading: selectLoadingState(state),
  isPageComplete: isPageComplete(state),
  isFirstPage: getCurrentPage(state) === PAGES.INDUSTRY,
  isSummaryPage: getCurrentPage(state) === PAGES.SUMMARY,
  isPaymentPage: getCurrentPage(state) === PAGES.PAYMENT,
  paymentInterval: getPaymentInterval(state),
  isPolicyPurchased: Boolean(getCurrentPage(state) === PAGES.PAYMENT && selectPurchasedPolicies(state)?.length),
  month1Owed: selectMonth1Owed(state),
  totalYearlyOwed: selectTotalYearlyOwed(state),
  monthlyPremium: selectMonthlyPremium(state),
  isRatePage: RATE_PAGES.includes(getCurrentPage(state)),
  applicationUrl: state?.application?.applicationUrl,
});

const FooterBar = styled.div`
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100vw;
  z-index: 6;
  background-color: white;
  border-top: 1px solid #d4d4dd;
  height: ${({ theme }) => theme.pageComponents.globalComponents.footerContainer.height};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  @media (max-width: 767px) {
    height: auto;
    padding: 16px 32px;
  }
  @media print {
    display: none;
  }
`;

/**
 * Necessary to ensure the fixed footer doesn't cover content.
 */
const FalseFooter = styled.div`
  height: ${({ theme }) => theme.pageComponents.globalComponents.footerContainer.height};
  box-sizing: border-box;
  @media (max-width: 767px) {
    height: 78px;
  }
`;

const ButtonCont = styled.div<{ justifyContent: string }>`
  box-sizing: border-box;
  margin: 0 auto;
  width: 100%;
  max-width: 580px;
  min-height: 46px;
  padding: 0 24px;
  display: flex;
  flex-direction: row;
  justify-content: ${({ justifyContent }) => justifyContent};
  align-items: center;
  gap: 24px;
  @media (min-width: 1025px) {
    margin: 0 0 0 ${SIDEBAR_DESKTOP_OFFSET};
    max-width: ${CONTENT_MAX_WIDTH};
    padding: 0;
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    margin: 0 0 0 ${SIDEBAR_TABLET_OFFSET};
    max-width: calc(100vw - ${SIDEBAR_TABLET_OFFSET});
    padding: 0 ${TABLET_HORIZONTAL_PADDING};
  }
  @media (max-width: 767px) {
    padding: 0;
    justify-content: space-around;
  }
  @media print {
    display: none;
  }
`;

const NextButton = styled(PrimaryButtonDarkTheme)<{ fullWidth?: boolean }>`
  box-shadow: none;
  margin: 0;
  ${({ fullWidth }) => fullWidth && 'width: 100%; max-width: none;'}
  :disabled {
    background-color: ${({ theme }) => theme.colors.neutral.light};
    color: ${({ theme }) => theme.colors.tertiary.sixty};
    opacity: 1;
  }

  @media (max-width: 767px) {
    flex: 1;
    max-width: 325px;
  }

  @media (min-width: 767px) {
    max-width: fit-content;
  }
`;

const SaveForLater = styled.div<{ hiddenOnMobile: boolean }>`
  && button {
    color: ${({ theme }) => theme.colors.primary.zero} !important;
  }
  @media (max-width: 767px) {
    display: ${({ hiddenOnMobile }) => (hiddenOnMobile ? 'none' : 'block')};
  }

  @media (min-width: 767px) {
    flex: none;
  }
`;

const PriceBlock = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const PriceLabel = styled.p`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.tertiary.sixty};
  line-height: 16px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const PriceAmount = styled.p`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.tertiary.zero};
  line-height: 26px;
  margin: 0;
`;

const PriceBlockSaveWrapper = styled.div`
  padding-top: 8px;
  && button {
    color: ${({ theme }) => theme.colors.primary.zero} !important;
    font-size: 13px !important;
  }
`;

export default connect(mapStateToProps, { handleFormSubmit, clearApplication })(Footer);
